import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { BOOK } from "@/constants/testIds";
import { Ruler, Scissors, Wrench, Heart, Sparkles, Upload, Check, ArrowLeft, ArrowRight, X, Flower2, Crown } from "lucide-react";

const services = [
  { key: "hemming", name: "Hemming", desc: "Length, cuffs, breaks", icon: Ruler },
  { key: "resize", name: "Resize", desc: "Take-in / let-out", icon: Scissors },
  { key: "zipper", name: "Zippers", desc: "Invisible & standard", icon: Wrench },
  { key: "bridal", name: "Bridal", desc: "Multi-fitting service", icon: Heart },
  { key: "wedding_dress", name: "Wedding Dress", desc: "Full wedding-dress fitting", icon: Crown },
  { key: "embroidery", name: "Embroidery", desc: "Monograms, motifs, appliqué", icon: Flower2 },
  { key: "repair", name: "Restoration", desc: "Rescue & repairs", icon: Sparkles },
  { key: "other", name: "Something else", desc: "Tell us more", icon: Sparkles },
];

const locations = ["Rhodes", "Ashfield", "West Ryde", "Charlestown"];
const timeSlots = ["09:30", "10:30", "11:30", "13:00", "14:00", "15:00", "16:00"];

export default function BookFitting() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [state, setState] = useState({
    service_type: params.get("service") || "",
    service_notes: "",
    preferred_date: "",
    preferred_time: "",
    location: "Rhodes",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    photos: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [bookingFee, setBookingFee] = useState(10);
  const [feeMap, setFeeMap] = useState({});

  useEffect(() => {
    api.get("/business").then(r => {
      setBookingFee(r.data?.booking_fee_aud || 10);
      setFeeMap(r.data?.service_fees_aud || {});
    }).catch(() => {});
  }, []);

  // Fee for the currently selected service (per-service override or default)
  const currentFee = state.service_type
    ? (feeMap[state.service_type] ?? bookingFee)
    : bookingFee;

  const canNext = useMemo(() => {
    if (step === 1) return !!state.service_type;
    if (step === 2) return !!state.preferred_date && !!state.preferred_time && !!state.location;
    if (step === 3) return true; // photos optional
    if (step === 4) return !!state.customer_name && !!state.customer_phone && !!state.customer_email;
    return false;
  }, [step, state]);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setState((s) => ({ ...s, photos: [...s.photos, data.data_url] }));
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", { ...state, origin_url: window.location.origin });
      // Save booking code so we can show it after Stripe redirect (payment success page)
      try { sessionStorage.setItem("last_booking_code", data.booking_code); } catch {}
      // Immediately redirect to Stripe checkout for the booking fee
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      setResult(data);
      toast.success("Booking received!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Something went wrong");
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <div className="w-16 h-16 rounded-full bg-[#0F172A] text-[#FFFFFF] flex items-center justify-center mx-auto"><Check size={28}/></div>
        <div className="overline mt-8">Booking received</div>
        <h1 className="font-serif-display text-5xl mt-4">Thank you.</h1>
        <p className="mt-6 text-[#4A4A4A]">Hedieh will review your request and message you on WhatsApp with a personalised quote and deposit link.</p>
        <div className="mt-10 border border-[#E2E8F0] p-8 bg-[#F8FAFC]">
          <div className="overline">Your booking code</div>
          <div data-testid={BOOK.successCode} className="font-serif-display text-4xl mt-3">{result.booking_code}</div>
          <p className="text-sm mt-4 text-[#4A4A4A]">Save this code — use it with your phone number to track your order.</p>
        </div>
        <div className="mt-10 flex gap-4 justify-center flex-wrap">
          <button onClick={() => navigate("/track")}
                  className="px-8 py-3 border border-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#0F172A] hover:text-[#FFFFFF]">
            Track My Order
          </button>
          <button onClick={() => navigate("/")}
                  className="px-8 py-3 bg-[#D4AF37] text-[#0F172A] text-xs uppercase tracking-[0.25em] hover:bg-[#B8941F]">
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="overline">Book a Fitting</div>
      <h1 className="font-serif-display text-4xl md:text-5xl mt-3">Step {step} of 4</h1>

      {/* progress */}
      <div className="flex items-center gap-2 mt-8">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className={`step-dot ${n === step ? "active" : n < step ? "done" : ""}`}/>
        ))}
      </div>

      {/* Steps */}
      <div className="mt-12">
        {step === 1 && (
          <div data-testid={BOOK.step1Service}>
            <div className="font-serif-display text-2xl mb-6">What can we help with?</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {services.map((s) => {
                const Icon = s.icon;
                const active = state.service_type === s.key;
                return (
                  <button key={s.key}
                          data-testid={BOOK.serviceOption(s.key)}
                          onClick={() => setState({ ...state, service_type: s.key })}
                          className={`text-left p-6 border transition-colors ${active ? "border-[#0F172A] bg-[#F8FAFC]" : "border-[#E2E8F0] bg-[#FFFFFF] hover:border-[#0F172A]"}`}>
                    <Icon size={22} strokeWidth={1} className="text-[#0F172A]"/>
                    <div className="font-serif-display text-xl mt-4">{s.name}</div>
                    <div className="text-sm text-[#4A4A4A] mt-1">{s.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid={BOOK.step2Date}>
            <div className="font-serif-display text-2xl mb-6">When would you like to visit?</div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="overline">Shop</label>
                <select data-testid={BOOK.locationSelect} value={state.location}
                        onChange={(e) => setState({ ...state, location: e.target.value })}
                        className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none">
                  {locations.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="overline">Preferred date</label>
                <input type="date" data-testid={BOOK.dateInput}
                       min={new Date().toISOString().slice(0, 10)}
                       value={state.preferred_date}
                       onChange={(e) => setState({ ...state, preferred_date: e.target.value })}
                       className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none"/>
              </div>
            </div>
            <div className="mt-8">
              <label className="overline">Time</label>
              <div className="grid grid-cols-4 md:grid-cols-7 gap-2 mt-3">
                {timeSlots.map(t => (
                  <button key={t} data-testid={`book-time-chip-${t.replace(":","")}`}
                          onClick={() => setState({ ...state, preferred_time: t })}
                          className={`py-3 text-sm border ${state.preferred_time === t ? "bg-[#0F172A] text-[#FFFFFF] border-[#0F172A]" : "border-[#E2E8F0] hover:border-[#0F172A]"}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid={BOOK.step3Photos}>
            <div className="font-serif-display text-2xl mb-2">Photos of your garment</div>
            <p className="text-sm text-[#4A4A4A] mb-6">Optional but helpful — a full-length shot and any detail we should see.</p>
            <label className="border border-dashed border-[#E2E8F0] hover:border-[#0F172A] p-10 flex flex-col items-center gap-3 cursor-pointer bg-[#F8FAFC]/50">
              <Upload size={24} className="text-[#0F172A]"/>
              <div className="text-sm">{uploading ? "Uploading..." : "Click to upload (max 6MB)"}</div>
              <input data-testid={BOOK.photoUpload} type="file" accept="image/*" className="hidden"
                     onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}/>
            </label>
            {state.photos.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mt-6">
                {state.photos.map((p, i) => (
                  <div key={i} className="relative aspect-square" data-testid={BOOK.photoPreview(i)}>
                    <img src={p} alt="upload" className="w-full h-full object-cover"/>
                    <button onClick={() => setState({ ...state, photos: state.photos.filter((_, x) => x !== i) })}
                            className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white flex items-center justify-center">
                      <X size={12}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="mt-8">
              <label className="overline">Notes for Hedieh (optional)</label>
              <textarea data-testid={BOOK.notesInput} rows={4}
                        value={state.service_notes}
                        onChange={(e) => setState({ ...state, service_notes: e.target.value })}
                        placeholder="Tell us about the piece, deadline, or any concerns…"
                        className="mt-2 w-full border border-[#E2E8F0] focus:border-[#0F172A] bg-white p-4 outline-none"/>
            </div>
          </div>
        )}

        {step === 4 && (
          <div data-testid={BOOK.step4Contact}>
            <div className="font-serif-display text-2xl mb-6">Your details</div>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="overline">Full name</label>
                <input data-testid={BOOK.nameInput} value={state.customer_name}
                       onChange={(e) => setState({ ...state, customer_name: e.target.value })}
                       className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none"/>
              </div>
              <div>
                <label className="overline">Phone (WhatsApp)</label>
                <input data-testid={BOOK.phoneInput} type="tel" value={state.customer_phone}
                       placeholder="+61 4xx xxx xxx"
                       onChange={(e) => setState({ ...state, customer_phone: e.target.value })}
                       className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none"/>
              </div>
              <div className="md:col-span-2">
                <label className="overline">Email</label>
                <input data-testid={BOOK.emailInput} type="email" value={state.customer_email}
                       onChange={(e) => setState({ ...state, customer_email: e.target.value })}
                       className="mt-2 w-full border-b-2 border-[#E2E8F0] focus:border-[#0F172A] bg-transparent py-3 outline-none"/>
              </div>
            </div>
            <div className="mt-8 p-6 bg-[#F8FAFC] border border-[#E2E8F0] text-sm text-[#4A4A4A]">
              <div className="flex items-baseline justify-between gap-4 mb-3">
                <strong className="text-[#0F172A]">Booking fee</strong>
                <span className="font-serif-display text-2xl text-[#0F172A]">A${bookingFee.toFixed(2)}</span>
              </div>
              A small refundable booking fee is charged now to secure your slot. Hedieh then reviews your photos and, if extra deposit is needed for materials, sends a personalised quote via WhatsApp before any work begins.
            </div>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="mt-12 flex justify-between">
        <button data-testid={BOOK.backBtn} disabled={step === 1}
                onClick={() => setStep(step - 1)}
                className="inline-flex items-center gap-2 px-6 py-3 border border-[#0F172A] text-xs uppercase tracking-[0.25em] disabled:opacity-30">
          <ArrowLeft size={14}/> Back
        </button>
        {step < 4 ? (
          <button data-testid={BOOK.nextBtn} disabled={!canNext}
                  onClick={() => setStep(step + 1)}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#0F172A] text-[#FFFFFF] text-xs uppercase tracking-[0.25em] disabled:opacity-30 hover:bg-[#B8941F]">
            Continue <ArrowRight size={14}/>
          </button>
        ) : (
          <button data-testid={BOOK.submitBtn} disabled={!canNext || submitting}
                  onClick={submit}
                  className="inline-flex items-center gap-2 px-8 py-3 bg-[#0F172A] text-[#FFFFFF] text-xs uppercase tracking-[0.25em] disabled:opacity-30 hover:bg-[#B8941F]">
            {submitting ? "Redirecting..." : "Continue to Payment"} <ArrowRight size={14}/>
          </button>
        )}
      </div>
    </div>
  );
}
